import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";
import apiClient from "../../utils/apiClient";
import MeetingLeaveConfirm from "../Classroom/MeetingLeaveConfirm";
import MeetingProviderRouter from "../Classroom/MeetingProviderRouter";
import useMeetingLeaveGuard, { inGratsClass } from "../../hooks/useMeetingLeaveGuard";
import { AppButton, EmptyState, Callout } from "../ui";
import { resolveSessionKind } from "./sessionKind";
import "./demo-classes.scss";

/**
 * Host console for BOTH live-session products — a demo class and a webinar.
 *
 * The backend answers with whatever the institute's meeting provider needs —
 * a Zoom SDK signature + ZAK, or a Grats Meet grant — and the router mounts
 * the matching embed. Either way the host lands in the room as host.
 *
 * ── Several speakers, one console ────────────────────────────────────────
 * A webinar can list additional speakers, and each of them opens THIS page.
 * The backend decides what each person gets — `meetingRole` is 'host' or
 * 'cohost' — so nothing here asks who the user is.
 *
 * On Grats Meet the two roles are the same rights and there is nothing to
 * explain. Zoom is the exception, and it is the one thing this page has to
 * say out loud: a Zoom meeting has exactly one host seat, so a co-speaker
 * joins as a participant and the primary speaker promotes them from the
 * participants panel. Dropping someone into Zoom as a muted attendee with no
 * warning, on a page called "host console", is how that becomes a support
 * call sixty seconds before a webinar starts.
 */
const DemoHostJoinPage = ({ kind = "demo", backPath }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const vocab = resolveSessionKind(kind);
  const routeBase = backPath || vocab.adminBase;

  const [zoomConfig, setZoomConfig] = useState(null);
  const [error, setError] = useState("");
  // Zoom co-speakers get one screen before the room. Acknowledging it is not
  // remembered: it is advice about THIS join, and the next one may be as host.
  const [coHostNoticeSeen, setCoHostNoticeSeen] = useState(false);
  // Only a Grats Meet room reports a status: its recording is in this browser.
  const [gratsStatus, setGratsStatus] = useState(null);
  const leaveGuard = useMeetingLeaveGuard(inGratsClass(gratsStatus));

  useEffect(() => {
    let cancelled = false;
    apiClient.get(`${vocab.apiBase}/sessions/${id}/host-join-config`)
      .then(({ data }) => {
        if (!cancelled) setZoomConfig(data);
      })
      .catch((err) => {
        if (cancelled) return;
        // The BE answers `errorCode`; `error` was the older shape.
        const code = err?.response?.data?.errorCode || err?.response?.data?.error;
        const msg = code === "NOT_LIVE"
          ? `This ${vocab.noun} is not live yet. Click 'Start ${vocab.noun}' first.`
          // NOT_A_SPEAKER carries its own sentence from the BE and is the one
          // refusal here that is about the person rather than the session.
          : err?.response?.data?.message || "Failed to load host join config.";
        setError(msg);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, kind]);

  const handleEnd = () => {
    // Best-effort: end it on the backend so the host slot frees up immediately.
    apiClient.post(`${vocab.apiBase}/sessions/${id}/end`).catch(() => {});
    leaveGuard.release();
    window.location.href = `${routeBase}/${id}`;
  };

  if (error) {
    return (
      <div className="demo-detail">
        <EmptyState
          title="Cannot start host console"
          description={error}
          action={
            <AppButton variant="default" onClick={() => navigate(`${routeBase}/${id}`)}>
              <ArrowLeft size={14} /> Back to {vocab.noun}
            </AppButton>
          }
        />
      </div>
    );
  }

  if (!zoomConfig) {
    return (
      <div className="demo-live">
        <div className="demo-live__loading">
          <span className="demo-public__pulse">Connecting to the live room…</span>
        </div>
      </div>
    );
  }

  if (zoomConfig.zoomCoHostManual && !coHostNoticeSeen) {
    return (
      <div className="demo-detail">
        <EmptyState
          icon={Users}
          title="You are joining as an additional speaker"
          description={
            <Callout tone="info">
              A Zoom meeting has one host, and it is held by the main{" "}
              {vocab.kind === "webinar" ? "speaker" : "instructor"}. You will
              join as a participant — ask them to make you a co-host from the
              participants panel and you will get your microphone, camera and
              screen share. On Grats Meet this step does not exist.
            </Callout>
          }
          action={
            <AppButton variant="default" onClick={() => setCoHostNoticeSeen(true)}>
              Join the {vocab.noun}
            </AppButton>
          }
        />
      </div>
    );
  }

  return (
    <>
      <MeetingProviderRouter
        joinConfig={zoomConfig}
        onMeetingEnd={handleEnd}
        // A dropped connection rejoins with a fresh grant instead of reaching
        // handleEnd, which ends the session for every attendee.
        onRejoin={async () => (await apiClient.get(`${vocab.apiBase}/sessions/${id}/host-join-config`)).data}
        onStateChange={({ status }) => setGratsStatus(status)}
        endLabel={`End ${vocab.noun}`}
        leaveUrl={`${window.location.origin}${routeBase}/${id}`}
        notifyOnBackgroundChat
      />
      <MeetingLeaveConfirm guard={leaveGuard} noun={vocab.noun} />
    </>
  );
};

export default DemoHostJoinPage;
